import { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import useTranslations from "../../../core/i18n/useTranslations";
import Button, {
  ButtonVariant,
} from "../../../ui-lib/components/Button/Button";
import PrimeModal from "../../../ui-lib/components/PrimeModal/PrimeModal";
import {
  Column,
  Row,
  Spacer,
  TwoColGrid,
} from "../../../components/Layout/Layout";
import RadioGroup from "../../../ui-lib/components/Radio/RadioGroup";
import {
  AdministratorDetails,
  PasswordConstraints,
} from "../../../core/api/administrators/types";
import {
  validateEmailIfExists,
  validateForbiddenChars,
  validateLowerCase,
  validateNumberCount,
  validatePhoneNumberIfExists,
  validateUpperCase,
} from "../../../core/helpers/validation";
import TextInput from "../../../ui-lib/components/Inputs/TextInput";
import PasswordInput from "../../../ui-lib/components/Inputs/PasswordInput";
import { notifyApiErrors } from "../../../core/helpers/helpers";
import useUser from "../../../core/user/useUser";
import {
  createAdministrator,
  getPasswordConstraints,
} from "../../../core/api/administrators/administrators";
import Checkbox from "../../../ui-lib/components/Checkbox/Checkbox";
import OrganisationFields from "./OrganisationFields";
import { getResponseCenters } from "../../../core/api/responsecenters/responsecenters";
import { IItemProp } from "../../../ui-lib/components/Dropdown/DropdownItem";

export const AddAdministrator = ({
  variant,
  onSubmit,
  onCopySubmit,
  onCancel,
  onClose,
  image,
  organizationId,
  creationType = "new",
  selectedType = "seller",
  showModal = true,
  defaultValues = {},
  isOpen = false,
  extraHeaderText,
  sellers,
  isLoading,
  publicArc,
}: {
  variant?: ButtonVariant;
  onSubmit?: (adminId: number) => void;
  onCopySubmit?: (data: AdministratorDetails) => void;
  onCancel?: () => void;
  onClose?: () => void;
  image?: string;
  organizationId?: number;
  creationType?: "copy" | "new";
  selectedType?: string;
  showModal?: boolean;
  defaultValues?: any;
  isOpen?: boolean;
  extraHeaderText?: string;
  sellers?: IItemProp[];
  isLoading?: boolean;
  publicArc?: boolean;
}) => {
  const t = useTranslations();
  const { authenticatedRequest, data: userData, config } = useUser();
  const [isAddAdministratorOpen, setIsAddAdministratorOpen] =
    useState<boolean>(isOpen);
  const [adminType, setAdminType] = useState<string>(selectedType);
  const [customerId, setCustomerId] = useState<number | undefined>(
    defaultValues.organizationId
  );
  const [arcId, setArcId] = useState<number | undefined>(
    defaultValues.responseCenterId
  );
  const [alarmRes, setAlarmRes] = useState<IItemProp[]>([]);
  const [passConstraint, setPassConstraint] = useState<PasswordConstraints>();
  const [sellersArc, setSellersArc] = useState<IItemProp[]>([]);
  const [sellerId, setSellerId] = useState<number | undefined>(
    defaultValues.sellerId
  );
  const [isArcLoading, setIsArcLoading] = useState(false);
  const superAdminType = config?.show.includes("SkygdSuperAdmin");
  const viserPermission = config?.show.includes("SensioHideFields");

  const methods = useForm<AdministratorDetails>({
    defaultValues: {
      type: adminType,
      ...defaultValues,
    },
  });

  const {
    control,
    formState: { isSubmitting, errors },
    handleSubmit,
    reset,
    setValue,
    trigger,
    getValues,
    clearErrors,
  } = methods;

  const handleClear = () => {
    reset();
    setAdminType("seller");
    setCustomerId(undefined);
    setSellerId(undefined);
  };

  const preselectSeller = () => {
    if (sellers?.length && !organizationId && adminType === "seller") {
      setValue("sellerId", Number(sellers[0].id));
      setSellerId(Number(sellers[0].id));
    }
  };

  const fetchPasswordComplexity = async (dataType: string, idToUse: number) => {
    try {
      const result = await getPasswordConstraints(
        idToUse,
        authenticatedRequest,
        dataType
      );
      setPassConstraint(result.data);
    } catch (error: any) {
      notifyApiErrors(error.response?.data?.errors);
    }
  };

  const getPasswordFetchData = () => {
    let dataType = "";
    let idToUse;
    const selectedArcId = Number(getValues("responseCenterId"));
    switch (adminType) {
      case "admin":
        dataType = "organizationId";
        idToUse = customerId;
        break;
      case "seller":
        dataType = "sellerId";
        idToUse = sellerId;
        break;
      case "respondent":
        dataType = "responseCenterId";
        idToUse = selectedArcId;
        setArcId(selectedArcId);
        setAlarmRes(
          alarmRes.map((arc) => ({
            ...arc,
            isSelected: arc.id === selectedArcId,
          }))
        );
        break;
      default:
        break;
    }
    return { dataType, idToUse };
  };

  const fetchAlarmReceptions = async () => {
    const queryData = {
      organizationId: customerId,
      includeSubCustomers: false,
      page: 1,
      pageSize: 10000,
      publicOnly: publicArc?.toString(),
    };
    setIsArcLoading(true);
    try {
      const data = await getResponseCenters(authenticatedRequest, queryData);
      const alarmItems = data.data.map(
        (item) =>
          ({
            id: item.id,
            name: item.name,
            isSelected: item.id === arcId,
          } as IItemProp)
      );
      setAlarmRes(alarmItems);
    } catch (error: any) {
      notifyApiErrors(error.response?.data?.errors);
    } finally {
      setIsArcLoading(false);
    }
  };

  useEffect(() => {
    if (customerId || publicArc) {
      fetchAlarmReceptions();
    }
  }, [customerId, adminType]);

  useEffect(() => {
    if (organizationId) {
      setSellerId(undefined);
      setValue("organizationId", organizationId);
      setCustomerId(organizationId);
    }
    if (adminType === "seller") {
      preselectSeller();
    }

    // set responseCenterId to refetch password complexity if admin type was changed
    if (arcId) {
      setValue("responseCenterId", arcId);
    }
  }, [organizationId, adminType, isAddAdministratorOpen]);

  useEffect(() => {
    if (sellers) {
      setSellersArc(
        sellers.map((item) => ({
          ...item,
          isSelected: item.id === sellerId,
        }))
      );
    }
  }, [sellers]);

  // get password complexity on changing belongings
  useEffect(() => {
    if (isAddAdministratorOpen) {
      const { idToUse, dataType } = getPasswordFetchData();

      if (idToUse) {
        fetchPasswordComplexity(dataType, +idToUse);
      }
    } else {
      setPassConstraint(defaultValues.passwordConstraints);
    }
  }, [arcId, customerId, sellerId, adminType, isAddAdministratorOpen]);

  useEffect(() => {
    // set default type "admin" if modal opened from organisotion page
    // or if current user have type "customer"
    if (
      (organizationId || userData?.type === "customer") &&
      !Object.keys(defaultValues).length
    ) {
      setAdminType("admin");
      setValue("type", "admin");
    }

    return () => {
      setArcId(undefined);
      setValue("responseCenterId", undefined);
    };
  }, [isAddAdministratorOpen]);

  const onCreateAdminSubmit = async (formData: AdministratorDetails) => {
    if (formData.type !== "admin") {
      delete formData.organizationId;
    }
    try {
      const responseData = await createAdministrator(
        formData,
        authenticatedRequest
      );
      onSubmit!(responseData.data);
      handleClear();
      setIsAddAdministratorOpen(false);
    } catch (error: any) {
      notifyApiErrors(error.response?.data?.errors);
    }
  };

  const radioAdminType = [
    ...((userData?.type === "seller" || superAdminType) && !organizationId
      ? [
          {
            id: 0,
            value: "seller",
            text: t("Common:seller"),
            description: t("Administrator:seller_type_description"),
            image: "super-user",
          },
        ]
      : []),
    {
      id: 1,
      value: "admin",
      text: t("Administrator:admin_type"),
      description: t("Administrator:admin_type_description"),
      image: "user",
    },
    ...(!organizationId
      ? [
          {
            id: 2,
            value: "respondent",
            text: t("Common:respondent"),
            description: t("Administrator:respondent_description"),
            image: "phone-incoming",
          },
        ]
      : []),
  ];

  const content = (
    <>
      <p>{t("Administrator:add_administrator_description")}</p>
      <Spacer size={32} />
      <Row type="fill" align="fill" style={{ alignSelf: "stretch" }}>
        <Controller
          name="type"
          control={control}
          render={() => (
            <RadioGroup
              name="template-type"
              items={radioAdminType}
              selectedValue={adminType}
              onChange={(value) => {
                setValue("type", value);
                setAdminType(value);
                setValue("password", "");
                clearErrors();
                // if switched to admin clear ResponseCenterId field
                if (value === "admin") {
                  setPassConstraint(undefined);
                  setValue("responseCenterId", undefined);
                  setValue("sellerId", undefined);
                  // need to re-fetch password complexity for customer
                  if (customerId) {
                    setValue("organizationId", customerId);
                  }
                }
                // clear organisationId if it was selected
                // previously and type switched to "seller"
                if (value === "seller") {
                  setValue("organizationId", undefined);
                  setValue("responseCenterId", undefined);
                  preselectSeller();
                }
                // clear sellerId if it was selected
                // previously and type switched to "respondent"
                if (value === "respondent") {
                  setPassConstraint(undefined);
                  setValue("sellerId", undefined);
                }
              }}
              fullWidth
              disabled={!!organizationId || creationType === "copy"}
              label={t("Administrator:admin_type")}
              labelHidden
            />
          )}
        />
      </Row>
      {organizationId && (
        <>
          <Spacer size={16} />
          <p style={{ color: "var(--Grey-600)" }}>
            {t("Administrator:organisation_admin_belongs")}
          </p>
        </>
      )}
      <Spacer size={32} />
      <OrganisationFields
        adminType={adminType}
        organizationId={organizationId}
        sellersArc={sellersArc}
        setSellersArc={setSellersArc}
        alarmRes={alarmRes}
        publicResponceCenter={publicArc}
        customerId={customerId}
        isLoading={isLoading}
        isArcLoading={isArcLoading}
        setSellerId={setSellerId}
        setCustomerId={setCustomerId}
        setArcId={setArcId}
      />
      <Spacer size={16} />
      <Row align="start">
        <Controller
          name="username"
          control={control}
          rules={{
            required: true,
            minLength: 1,
            maxLength: 60,
          }}
          render={({ field }) => (
            <TextInput
              required
              disableBrowserAutocomplete={true}
              inputRef={field.ref}
              label={t("Administrator:general_input_username")}
              placeholder={t("Common:enter_username")}
              value={field.value}
              onChange={(e) => {
                setValue("username", e.target.value);
                trigger(field.name);
              }}
              validationError={
                errors?.username && t("Errors:input_organization_name")
              }
            />
          )}
        />
        <Spacer size={32} />
        <Controller
          name="password"
          control={control}
          rules={
            passConstraint
              ? {
                  required: true,
                  minLength: passConstraint?.minLength,
                  validate: {
                    lowerCase: (v) => validateLowerCase(v, passConstraint),
                    upperCase: (v) => validateUpperCase(v, passConstraint),
                    countNumbers: (v) => validateNumberCount(v, passConstraint),
                    forbiddenChars: (v) =>
                      validateForbiddenChars(v, passConstraint),
                  },
                }
              : undefined
          }
          render={({ field }) => (
            <PasswordInput
              description={
                (adminType === "respondent" || adminType === "admin") &&
                !passConstraint
                  ? t("Common:first_choose_relation")
                  : ""
              }
              disableBrowserAutocomplete={true}
              required
              withHint
              withIndicator
              passwordConstraints={passConstraint}
              id="password"
              inputRef={field.ref}
              label={t("Common:login_input_password")}
              placeholder={
                passConstraint
                  ? t(
                      "Administrator:password_constraints_minimum_length"
                    ).replace("{0}", `${passConstraint.minLength}`)
                  : ""
              }
              value={field.value}
              invalid={!!errors?.password}
              onChange={field.onChange}
              disabled={!passConstraint}
            />
          )}
        />
      </Row>
      <Spacer size={16} />
      <Row>
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <TextInput
              label={t("Common:name")}
              placeholder={t("Administrator:enter_name")}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
        <Spacer size={32} />
        <Controller
          name="initials"
          control={control}
          render={({ field }) => (
            <TextInput
              label={t("Common:initials")}
              placeholder={t(
                "Administrator:general_input_initials_placeholder"
              )}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </Row>
      {creationType === "new" && (
        <>
          <Spacer size={16} />
          <Row align="start">
            <Column>
              <Controller
                name="email"
                control={control}
                rules={{
                  maxLength: 60,
                  validate: validateEmailIfExists,
                }}
                render={({ field }) => (
                  <TextInput
                    inputRef={field.ref}
                    placeholder={t("Common:placeholder_email")}
                    label={t("Contacts:input_email_address")}
                    value={field.value}
                    onChange={field.onChange}
                    validationError={
                      errors?.email && t("Errors:input_email_format")
                    }
                  />
                )}
              />
            </Column>
            <Spacer size={32} />
            <Column>
              <Controller
                name="telephone"
                control={control}
                rules={{
                  validate: validatePhoneNumberIfExists,
                }}
                render={({ field }) => (
                  <TextInput
                    inputRef={field.ref}
                    placeholder={t("Common:phone_number_placeholder")}
                    label={t("Filters:Telephone")}
                    value={field.value}
                    onChange={field.onChange}
                    validationError={
                      errors?.telephone &&
                      t("Objects:Validation_identifier_format")
                    }
                  />
                )}
              />
            </Column>
          </Row>
        </>
      )}
      {creationType === "copy" && !viserPermission && (
        <>
          <Spacer size={32} />

          <label>{t("Administrator:copy_settings_label")}</label>

          <Spacer size={8} />
          <TwoColGrid>
            <div style={{ width: "390px" }}>
              <Controller
                control={control}
                name="mainSettings"
                render={({ field }) => (
                  <Checkbox
                    label={t("Administrator:MainSettings")}
                    checked={!!field.value}
                    onChange={() => {
                      setValue(field.name, !field.value);
                    }}
                    style={{ padding: 0 }}
                  />
                )}
              />
            </div>
            <Controller
              control={control}
              name={
                adminType === "respondent"
                  ? "adminRespondentGroups"
                  : "adminGUIConfigs"
              }
              render={({ field }) => (
                <Checkbox
                  label={t(
                    `Administrator:${
                      adminType === "respondent"
                        ? "AdminRespondentGroups"
                        : "AdminGUIConfigs"
                    }`
                  )}
                  checked={!!field.value}
                  onChange={() => {
                    setValue(field.name, !field.value);
                  }}
                  style={{ padding: 0 }}
                />
              )}
            />
          </TwoColGrid>
          <TwoColGrid>
            <div style={{ width: "390px" }}>
              <Controller
                control={control}
                name="menuAlternatives"
                render={({ field }) => (
                  <Checkbox
                    label={t("Administrator:MenuAlternatives")}
                    checked={!!field.value}
                    onChange={() => {
                      setValue(field.name, !field.value);
                    }}
                    style={{ padding: 0 }}
                  />
                )}
              />
            </div>
            {adminType !== "respondent" && (
              <Controller
                control={control}
                name="adminRoles"
                render={({ field }) => (
                  <Checkbox
                    label={t("Administrator:AdminRoles")}
                    checked={!!field.value}
                    onChange={() => {
                      setValue(field.name, !field.value);
                    }}
                    style={{ padding: 0 }}
                  />
                )}
              />
            )}
          </TwoColGrid>
          <TwoColGrid>
            <div style={{ width: "390px" }}>
              <Controller
                control={control}
                name="forcePasswordReset"
                render={({ field }) => (
                  <Checkbox
                    label={t("Administrator:ForcePasswordReset")}
                    checked={!!field.value}
                    onChange={() => {
                      setValue(field.name, !field.value);
                    }}
                    style={{ padding: 0 }}
                  />
                )}
              />
            </div>
          </TwoColGrid>

          {!showModal && (
            <>
              <Spacer size={32} />
              <Row align="start" type="left">
                <Button
                  variant="primary"
                  text={t("Administrator:create_administrator")}
                  onClick={() => handleSubmit(onCopySubmit!)()}
                />
                <Spacer size={16} />

                <Button
                  variant="secondary"
                  text={t("Common:cancel")}
                  onClick={onCancel}
                />
              </Row>
              <Spacer size={16} />
            </>
          )}
        </>
      )}
    </>
  );

  if (!showModal) {
    return content;
  }

  return (
    <>
      {variant && (
        <Button
          variant={variant}
          image={image}
          text={t("Administrator:add_administrator")}
          onClick={() => {
            setIsAddAdministratorOpen(true);
          }}
          loading={isLoading}
          disabled={isLoading}
          dataCy="add_admin"
        />
      )}
      <PrimeModal
        withFooter
        withHeader
        dataCy="add_admin_modal"
        fixedWidth="836px"
        loading={isSubmitting}
        isOpen={isAddAdministratorOpen}
        header={
          <>
            {t("Administrator:add_administrator")}
            {extraHeaderText && (
              <span style={{ opacity: 0.5 }}>{` ${extraHeaderText}`}</span>
            )}
          </>
        }
        onClose={() => {
          onClose?.();
          handleClear();
          setPassConstraint(undefined);
          setIsAddAdministratorOpen(false);
        }}
        submitBtn={{
          text: t("Administrator:create_administrator"),
          onClick: () =>
            onCopySubmit
              ? handleSubmit(onCopySubmit)()
              : handleSubmit(onCreateAdminSubmit)(),
        }}
        cancelBtn={{
          text: t("Common:cancel"),
          onClick: () => {
            onClose?.();
            handleClear();
            setIsAddAdministratorOpen(false);
          },
        }}
      >
        <FormProvider {...methods}>{content}</FormProvider>
      </PrimeModal>
    </>
  );
};
